// What a new client should know about working with us, shown before they
// submit onboarding. Written from 14 recent onboards (10-2026): most of the
// back-and-forth was call requests, creative taste edits, CRM setup, ZIP
// churn and Facebook card charges nobody had explained.
//
// Keep the five points short and benefit-led. Anything longer belongs in
// COMMON_QUESTIONS, and the same answers are seeded into Guru's knowledge
// (seed_knowledge_facts.py) so the chatbot says the same thing.
import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export const CLIENTS_EMAIL = 'clients@leadgurus.com';

export const HOW_WE_WORK_POINTS = [
  {
    title: 'Your creative runs on our system',
    body: (
      'Our ads are built from hundreds of millions in home-improvement ad spend. We own the '
      + 'strategy, design, colors, imagery and testing. Your input matters most on two things: '
      + 'your offer, and anything that doesn\'t represent your brand accurately (logo, company '
      + 'name, products, claims). One round of revisions is included, so please send all your '
      + 'notes in one pass.'
    ),
  },
  {
    title: 'One strategy call in your first 30 days',
    body: (
      'You get one 30-minute call within your first 30 days. We usually recommend about two '
      + 'weeks after launch, once there is real data to review. After that, the calls you can '
      + 'book depend on your monthly ad spend; your allowance is under Schedule a Meeting in the '
      + 'portal. At lower spend there usually isn\'t enough data for a call to add much, so '
      + 'email and Ask Guru are the fastest way to get answers.'
    ),
  },
  {
    title: 'Connect your sales data. This part is on you',
    body: (
      'Your dashboard shows leads and cost per lead from day one. To judge real results '
      + '(appointments, sales, revenue) we need your CRM or backend data connected to the '
      + 'portal. Until it is connected we can\'t see how campaigns turn into sales, so we can\'t '
      + 'review or troubleshoot performance at that level. Getting this set up early is the '
      + 'most important thing you can do for your campaigns.'
    ),
  },
  {
    title: 'ZIP codes = where you can serve. We handle performance',
    body: (
      'Only give us ZIP codes your team can actually serve. Our system tracks every ZIP and '
      + 'removes weak ones automatically, and it adjusts budgets, targeting and creative every '
      + 'day. Please don\'t add or remove ZIPs to chase results; every change resets learning. '
      + 'If your service area or budget changes, update it in the portal (ZIP Coverage and '
      + 'Budget Center) so it is tracked and applied quickly.'
    ),
  },
  {
    title: `One inbox for everything: ${CLIENTS_EMAIL}`,
    body: (
      'Send questions, integration invites and changes there; the whole team sees it and we '
      + 'reply quickly. Your dashboard updates in real time, and Ask Guru, our AI assistant, '
      + 'answers performance questions 24/7.'
    ),
  },
];

export const COMMON_QUESTIONS = [
  {
    q: 'Why is Facebook charging my card?',
    a: (
      'If you fund your own ad spend, Facebook bills your card directly as spend builds. The '
      + 'charges start small and grow as each billing threshold rises. That is normal: those '
      + 'charges are your ad spend. Our service fee is a percentage of ad spend, billed the way '
      + 'your agreement sets out, and every invoice and receipt is under Invoices in the portal.'
    ),
  },
  {
    q: 'How does self-booking work?',
    a: (
      'Leads can book an appointment straight from our landing page, using the days and times '
      + 'you set in the Self-Book Schedule step. You can change your schedule anytime; email us '
      + 'if you want self-booking turned off.'
    ),
  },
  {
    q: 'Who sets up lead delivery to my CRM?',
    a: (
      'We support most home-improvement CRMs (LeadPerfection, i360, Hatch, Builder Prime, '
      + 'JobNimbus, LeadConduit, Zapier and more). Pick yours in the CRM & Routing step and '
      + `send any integration invites to ${CLIENTS_EMAIL}. Use Send Test Lead on the Lead `
      + 'Delivery page to check fields land where you expect.'
    ),
  },
  {
    q: 'Why did I get a lead from outside my area?',
    a: (
      'Facebook and Google target people who live in or were recently in your area, so a small '
      + 'share of leads come from just outside it. That is how the platforms work. Our landing '
      + 'pages check ZIP codes to filter most of it, and those leads are labeled out of area.'
    ),
  },
  {
    q: 'When do my ads go live?',
    a: (
      'Usually 7–10 business days after onboarding is complete and your creative is approved. '
      + 'You will get an email the moment campaigns are live, and another when your first leads '
      + 'arrive.'
    ),
  },
  {
    q: 'Can I get a custom report?',
    a: (
      'Everything is in your dashboard in real time, and every lead and ZIP-level result can be '
      + 'exported from the Leads and ZIP Code Performance tables. Ask Guru can pull any number '
      + 'for you on the spot.'
    ),
  },
  {
    q: 'How do I pause or cancel?',
    a: (
      'You can pause anytime through the portal; we pause within 24 hours. The agreement is '
      + 'month to month with 30 days\' written notice to cancel.'
    ),
  },
];

export function HowWeWorkPoints({ dense = false }) {
  return (
    <Stack spacing={dense ? 1.25 : 1.75}>
      {HOW_WE_WORK_POINTS.map((p, i) => (
        <Stack key={p.title} direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              flexShrink: 0, width: 24, height: 24, borderRadius: '50%', mt: '1px',
              bgcolor: '#111827', color: '#fff', fontSize: 12, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {i + 1}
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>{p.title}</Typography>
            <Typography variant="body2" color="text.secondary">{p.body}</Typography>
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

export function CommonQuestions() {
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Common questions</Typography>
      {COMMON_QUESTIONS.map((item) => (
        <Accordion
          key={item.q} disableGutters elevation={0}
          sx={{ border: '1px solid #e2e8f0', '&:not(:last-child)': { borderBottom: 0 }, '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>{item.q}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="body2" color="text.secondary">{item.a}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

// The wizard's first step. `acknowledged` is saved in the onboarding payload;
// Submit stays disabled until it is checked.
export function StepHowWeWork({ acknowledged, onChange }) {
  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6">How we work together</Typography>
        <Typography variant="body2" color="text.secondary">
          A quick read before we start. It covers what we handle, what we need from you, and the
          fastest way to get answers.
        </Typography>
      </Box>
      <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <HowWeWorkPoints />
      </Box>
      <CommonQuestions />
      <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: acknowledged ? 'success.light' : '#e2e8f0' }}>
        <FormControlLabel
          control={<Checkbox checked={!!acknowledged} onChange={(e) => onChange(e.target.checked)} />}
          label={<Typography variant="body2" fontWeight={600}>I understand how Lead Gurus works.</Typography>}
        />
      </Box>
    </Stack>
  );
}

// Short version for the creative review page, where the taste edits happen.
export function CreativeOwnershipNote() {
  return (
    <Alert severity="info" variant="outlined" sx={{ bgcolor: '#f8fafc' }}>
      <Typography variant="body2">
        <strong>How creative review works.</strong> Our ads follow a system built from hundreds of
        millions in home-improvement ad spend, so we own the strategy, design, colors and imagery.
        Please flag anything about your <strong>offer</strong> or anything that{' '}
        <strong>doesn&apos;t represent your brand accurately</strong> (logo, company name, products,
        claims). One round of revisions is included, so please send all your notes in one pass.
      </Typography>
    </Alert>
  );
}
